import { useEffect, useRef } from 'react'
import type { ShareableSizeId } from '../card/layout'
import { SHARE_LABELS, SHAREABLE_SIZES } from '../card/layout'
import type { ShareStatus } from '../hooks/useShareCard'

/**
 * Pick a format, then share.
 *
 * Purely presentational — it takes rendered preview URLs and calls back. Nothing
 * here touches `navigator.share` or a canvas, which is what lets it into the
 * design-system bundle: a preview iframe can render this with two placeholder
 * images and no browser capability it does not have.
 *
 * The rendered image stays visible the whole time, and that is load-bearing
 * rather than decorative. This product's traffic arrives inside Instagram and
 * Facebook in-app browsers, where `navigator.share` and `<a download>` can both
 * be missing with no way to detect it in advance. A visible image can always be
 * long-pressed and saved, so the sheet cannot dead-end even when every
 * programmatic path fails.
 */

interface ShareSheetProps {
  open: boolean
  status: ShareStatus
  previews: Partial<Record<ShareableSizeId, string>>
  error: string | null
  /** Something the share did that the user could not see — e.g. a clipboard write. */
  notice: string | null
  altText: string
  onPick: (size: ShareableSizeId) => void
  onSave: (size: ShareableSizeId) => void
  onClose: () => void
}

export function ShareSheet({
  open,
  status,
  previews,
  error,
  notice,
  altText,
  onPick,
  onSave,
  onClose,
}: ShareSheetProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!open) return
    closeRef.current?.focus()

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-title"
    >
      <button
        type="button"
        aria-label="Close share"
        onClick={onClose}
        className="absolute inset-0 bg-[color:var(--c-bg-void)]/70 backdrop-blur-sm"
      />

      <div className="relative flex max-h-[88dvh] w-full max-w-md flex-col rounded-t-[var(--radius-xl)] border border-[color:var(--c-border)] bg-[color:var(--c-bg-surface)] shadow-[var(--shadow-lg)] sm:rounded-[var(--radius-xl)]">
        <header className="flex items-start justify-between gap-4 border-b border-[color:var(--c-border-subtle)] px-5 py-4">
          <div>
            <h2
              id="share-title"
              className="text-[length:var(--text-title)] text-[color:var(--c-text-primary)]"
            >
              Share your result
            </h2>
            <p className="mt-1 text-[length:var(--text-label)] text-[color:var(--c-text-tertiary)]">
              The card shows your odds and the shape of the projection — never your actual
              numbers.
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid shrink-0 place-items-center rounded-[var(--radius-full)] text-[color:var(--c-text-tertiary)] transition-colors duration-[var(--dur-fast)] hover:bg-[color:var(--c-bg-hover)] hover:text-[color:var(--c-text-primary)]"
            style={{ width: 'var(--touch-target)', height: 'var(--touch-target)' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="flex flex-col gap-4 overflow-y-auto px-5 py-4">
          {error !== null && (
            <p
              role="alert"
              className="rounded-[var(--radius-md)] border border-[color:var(--c-negative)] px-3 py-2 text-[length:var(--text-label)] text-[color:var(--c-negative)]"
            >
              {error}
            </p>
          )}

          {/*
            The card's description lives here once, not on each thumbnail.
            Both previews show the same card, so putting `altText` on the two
            images made each button's accessible name the whole description —
            disclaimer included — announced twice, with the one word that
            distinguishes the buttons ("Post" / "Story") buried at the end of it.
            One description, referenced by both, and the images are decorative.
          */}
          <p id="share-card-description" className="sr-only">
            {altText}
          </p>

          <div className="grid grid-cols-2 gap-4">
            {SHAREABLE_SIZES.map((size) => {
              const url = previews[size]
              return (
                <div key={size} className="flex flex-col gap-2 p-2 text-left">
                  {/*
                    One box height for both, with the card CONTAINED inside it
                    rather than cropped. The two formats are 4:5 and 9:16, so
                    giving each thumb its own aspect ratio made the story column
                    half again as tall as the post column and left the two labels
                    on different lines with a dead gap beside the shorter one.
                    Cropping to a common ratio would be worse: this is a preview
                    of an image whose whole point is what it says at the bottom.
                  */}
                  {/*
                    overflow-hidden is load-bearing, not cosmetic: aspect-ratio
                    sets a PREFERRED height, and the box's automatic minimum size
                    still lets content override it. Without it the 9:16 image
                    stretched its own container back to 9:16 and the two thumbs
                    went back to different heights.
                  */}
                  <div className="w-full overflow-hidden" style={{ aspectRatio: '3 / 4' }}>
                    {url !== undefined && (
                      <img
                        src={url}
                        // Decorative: the button names the format and
                        // aria-describedby carries the card's description.
                        alt=""
                        // Explicit 100%/100% rather than max-*: a percentage
                        // max-height does not reliably resolve against a box whose
                        // height comes from aspect-ratio, and the story card was
                        // silently cropped to the post's height — losing exactly
                        // the disclaimer this preview exists to show.
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    )}
                  </div>
                  <span className="text-[length:var(--text-label)] text-[color:var(--c-text-primary)]">
                    {SHARE_LABELS[size].title}
                  </span>
                  <span className="text-[length:var(--text-micro)] text-[color:var(--c-text-tertiary)]">
                    {SHARE_LABELS[size].hint}
                  </span>

                  {/*
                    Two actions, not one, and Save is not a fallback hidden
                    behind a failed share. Handing the file to the OS reaches
                    Stories well, but a FEED post generally wants the image in
                    the camera roll first — so the route that actually reaches
                    the surface the 4:5 card is cut for has to be visible.
                  */}
                  <div className="mt-1 flex gap-2">
                    <button
                      type="button"
                      disabled={url === undefined}
                      onClick={() => onPick(size)}
                      aria-label={`Share ${SHARE_LABELS[size].title}`}
                      aria-describedby="share-card-description"
                      className="flex-1 rounded-[var(--radius-sm)] bg-[color:var(--c-accent)] px-2 py-1.5 text-center text-[length:var(--text-micro)] text-[color:var(--c-text-on-accent)] transition-opacity duration-[var(--dur-fast)] hover:opacity-90 disabled:opacity-50"
                    >
                      Share
                    </button>
                    <button
                      type="button"
                      disabled={url === undefined}
                      onClick={() => onSave(size)}
                      aria-label={`Save ${SHARE_LABELS[size].title} to your device`}
                      aria-describedby="share-card-description"
                      className="flex-1 rounded-[var(--radius-sm)] border border-[color:var(--c-border)] px-2 py-1.5 text-center text-[length:var(--text-micro)] text-[color:var(--c-text-secondary)] transition-colors duration-[var(--dur-fast)] hover:bg-[color:var(--c-bg-hover)] hover:text-[color:var(--c-text-primary)] disabled:opacity-50"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          <p
            className="text-[length:var(--text-micro)] text-[color:var(--c-text-muted)]"
            aria-live="polite"
          >
            {notice !== null
              ? notice
              : status === 'preparing'
                ? 'Drawing your card…'
                : 'Share opens your phone\u2019s share sheet. Save writes the image to your device \u2014 which is what an Instagram feed post needs.'}
          </p>
        </div>
      </div>
    </div>
  )
}
